import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { Lightbox } from "@/components/Lightbox";
import { SERVICES, COMPANY } from "@/lib/site";
import { Check, ArrowRight, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LONG_COPY = {
  "industrial-powder-coating": {
    intro: "Industrial powder coating is what our floor was built for. From weekly OEM programs to structural weldments up to 20 feet long, our two-oven / two-booth line runs at production tempo without sacrificing finish quality.",
    sections: [
      { h: "Built for High-Volume Programs", p: "Weekly PO releases, JIT scheduling, and bonded inventory of your standard colors are all supported. We run production tickets against written specs — coating chemistry, mil-build, and QC gates are documented per lot." },
      { h: "Chemistry That Matches Your End Use", p: "TGIC polyester, hybrid, epoxy, urethane, and Super-Durable Class chemistries in stock or specified per program. We recommend the right powder for corrosion, UV, chemical, and abrasion resistance based on your part's real-world environment." },
      { h: "Large-Part Capacity", p: "Parts up to 20 feet long and 10 feet wide clear our booths and ovens. Forklifts and overhead handling move heavy weldments through the process safely. 3D-printed masking fixtures repeat run after run." },
    ],
  },
  "commercial-powder-coating": {
    intro: "Commercial powder coating for signage frames, storefront fixtures, restaurant equipment, HVAC housings, and light commercial assemblies. Consistent color, consistent finish, on a schedule that matches your build-out.",
    sections: [
      { h: "Consistent Across Locations", p: "For multi-site rollouts we lock down the powder SKU and color batch to guarantee every fixture matches — whether it ships now or six months into the program." },
      { h: "Rack and Batch Processing", p: "Small parts run on custom racks; large fixtures run on carts. Either way you get uniform coverage and controlled cure." },
      { h: "Tenant Improvement Timelines", p: "Retail and hospitality projects live and die by schedule. We coordinate directly with GCs and installers so the coating step never becomes the bottleneck." },
    ],
  },
  "architectural-powder-coating": {
    intro: "AAMA 2603 and AAMA 2604 class architectural powder coating for exterior railings, façades, canopies, storefronts, sunshades, and structural steel. Weatherable powders backed by manufacturer warranty.",
    sections: [
      { h: "Weatherable Chemistries", p: "Super-Durable polyester and fluoropolymer-modified powders engineered for color and gloss retention in real-world UV, humidity, and temperature cycling." },
      { h: "Coordinated with GCs & Installers", p: "We work directly with architects, engineers, and general contractors. Shop drawings, submittal packages, and finish samples supported." },
      { h: "Structural Length Capacity", p: "Handrails, mullions, and columns up to 20 feet handled without splicing or shipping to a second shop." },
    ],
  },
  "media-blasting": {
    intro: "Media blasting is not an afterthought here — it is a dedicated production step. Our walk-in blast room handles structural steel, weldments, and delicate substrates with the right media, pressure, and profile for the job.",
    sections: [
      { h: "Right Media for the Substrate", p: "Steel shot, steel grit, aluminum oxide, garnet, and specialty media stocked. We select media based on substrate hardness, contamination type, and required anchor profile." },
      { h: "SSPC-Ready Prep", p: "SSPC-SP profile targets available on request. We prep to SSPC-SP 6 commercial and SSPC-SP 10 near-white standards for demanding coatings and industrial environments." },
      { h: "Prep, Not Just Blast", p: "Blasting is coordinated with immediate coating or protective wrap to prevent flash rust. Time between blast and coat is controlled." },
    ],
  },
  "chemical-stripping": {
    intro: "A dedicated chemical strip tank removes old powder coating, e-coat, enamels, and paint without warping thin-gauge parts or destroying the substrate. Ideal for restoration, rework, and repeated production parts.",
    sections: [
      { h: "Non-Destructive Removal", p: "Chemical immersion strips coating without the heat, impact, or profile change of aggressive blasting — safer for aluminum, thin steel, and cast substrates." },
      { h: "Rework Made Practical", p: "Reject parts, mis-colored runs, and warranty returns are stripped and re-coated in a single facility, cutting logistics and rework cost." },
      { h: "Reduced Media Consumption", p: "Chemical strip before a light blast reduces media consumption and speeds throughput on heavily coated parts." },
    ],
  },
  "motorcycle-powder-coating": {
    intro: "Show-quality powder coating for motorcycle frames, swingarms, wheels, calipers, engine components, and small parts. Precision masking, custom colors, and lead times that respect your build.",
    sections: [
      { h: "Custom Colors & Finishes", p: "Candy, metallic, textured, and translucent powder finishes matched to your build. We stock and reference RAL, custom chips, and manufacturer colors." },
      { h: "Precision Masking", p: "Bearing surfaces, threads, brake mounting faces, and headset tubes masked with 3D-printed plugs for perfect edges and zero cleanup." },
      { h: "Builder-Friendly Lead Times", p: "We understand that a bike doesn't roll until every part is coated. Frames, wheels, and small parts run together on your timeline." },
    ],
  },
  "automotive-powder-coating": {
    intro: "OEM-quality powder coating for automotive frames, suspension components, wheels, brake calipers, engine bays, and full restorations. High-temp coatings and precise masking on every part.",
    sections: [
      { h: "Chassis & Roll Cage Capacity", p: "Our booth and oven length accommodates full frames, roll cages, and long suspension links without splicing or shipping to a second facility." },
      { h: "High-Temp Coatings", p: "Headers, brake calipers, and engine components coated with high-temperature-rated powders that hold color and film integrity under real driving conditions." },
      { h: "Restoration & Aftermarket", p: "Restoration shops and aftermarket manufacturers use us as a repeatable finishing partner — one QC standard across every batch." },
    ],
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find(s => s.slug === slug);
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    if (!service) return;
    let cancelled = false;
    axios.get(`${API}/services/${slug}/album`)
      .then(r => {
        if (cancelled) return;
        setAlbum(r.data?.album || null);
        setPhotos(Array.isArray(r.data?.items) ? r.data.items : []);
      })
      .catch(() => { if (!cancelled) { setAlbum(null); setPhotos([]); } });
    return () => { cancelled = true; };
  }, [slug, service]);

  if (!service) return <Navigate to="/services" replace />;

  const copy = LONG_COPY[slug] || { intro: service.short, sections: [] };
  const related = SERVICES.filter(s => s.slug !== slug).slice(0, 3);

  // Hero uses album cover if linked, otherwise static image
  const heroImage = album?.cover_image_url || service.image;
  // Preview strip: up to 4 photos (excluding the cover which is already the hero)
  const previewStrip = photos.filter(p => p.image_url !== album?.cover_image_url).slice(0, 4);
  // Lightbox items for the full grid
  const lbItems = photos.map(p => ({ src: p.image_url, title: p.title, description: p.description }));

  return (
    <div data-testid={`service-detail-${slug}`}>
      <PageHero
        eyebrow={service.tag}
        title={service.title}
        subtitle={copy.intro}
        image={heroImage}
      />

      {/* Preview strip — small thumbnails right under the hero when photos exist */}
      {previewStrip.length > 0 && (
        <section className="border-b border-white/10 bg-[#0B0B0D] px-6 lg:px-10 py-6" data-testid="service-preview-strip">
          <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide">
            <div className="eyebrow shrink-0 flex items-center gap-2 text-[#FF5722]">
              <Camera className="w-3 h-3" /> Recent Work
            </div>
            {previewStrip.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setLightboxIdx(photos.findIndex(x => x.id === p.id))}
                className="group relative shrink-0 w-32 h-24 md:w-40 md:h-28 border border-white/10 overflow-hidden hover:border-[#FF5722] transition-colors"
                data-testid={`service-preview-${i}`}
              >
                <img src={p.image_url} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
              </button>
            ))}
            {photos.length > 4 && (
              <a href="#service-full-gallery" className="shrink-0 font-display text-sm uppercase tracking-widest text-white/70 hover:text-white transition-colors">
                +{photos.length - 4} more →
              </a>
            )}
          </div>
        </section>
      )}

      <section className="section px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          {copy.sections.map(s => (
            <div key={s.h}>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight text-white">{s.h}</h2>
              <p className="mt-4 text-zinc-300 leading-relaxed max-w-2xl">{s.p}</p>
            </div>
          ))}

          <div>
            <div className="eyebrow mb-4">What's Included</div>
            <ul className="space-y-3">
              {service.bullets.map(b => (
                <li key={b} className="flex items-start gap-3 text-zinc-200 text-lg">
                  <Check className="w-5 h-5 text-[#FF5722] mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="border border-white/10 p-8 sticky top-32 bg-[#0B0B0D]">
            <div className="eyebrow mb-3">Get a Quote</div>
            <div className="font-display text-3xl tracking-tight leading-tight">
              Ready to <span className="text-[#FF5722]">coat this project?</span>
            </div>
            <p className="text-zinc-400 text-sm mt-4">
              Send drawings, photos, or a part list. Response within one business day.
            </p>
            <Link to="/quote" className="btn-primary w-full mt-6 justify-between" data-testid="service-detail-quote">
              Request a Quote <ArrowRight className="w-4 h-4" />
            </Link>
            <a href={COMPANY.phoneHref} className="btn-ghost w-full mt-3 justify-between">
              Call {COMPANY.phone} <ArrowRight className="w-4 h-4" />
            </a>

            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="eyebrow mb-3">Related Services</div>
              <ul className="space-y-2">
                {related.map(r => (
                  <li key={r.slug}>
                    <Link to={`/services/${r.slug}`} className="text-zinc-300 hover:text-[#FF5722] transition-colors text-sm">
                      → {r.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </section>

      {/* Full-width photo grid — only when linked album has photos */}
      {photos.length > 0 && (
        <section id="service-full-gallery" className="section px-6 lg:px-10 border-t border-white/10" data-testid="service-full-gallery">
          <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-2 text-[#FF5722]">Project Gallery</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter">
                {service.title} <span className="text-white/60">Portfolio</span>
              </h2>
            </div>
            <div className="text-sm text-zinc-400">{photos.length} photo{photos.length === 1 ? "" : "s"}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setLightboxIdx(i)}
                className="group relative aspect-square border border-white/10 overflow-hidden hover:border-[#FF5722] transition-colors"
                data-testid={`service-gallery-photo-${i}`}
              >
                <img src={p.image_url} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-xs font-display uppercase tracking-widest text-white line-clamp-1">{p.title}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {lightboxIdx !== null && lbItems.length > 0 && (
        <Lightbox
          items={lbItems}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndex={setLightboxIdx}
        />
      )}

      <CTABand />
    </div>
  );
}
